import {UserConfig} from "../../types/UserConfig";
import {AccountStore} from "../../account/store/AccountStore";
import {AccountBriefRecords} from "../../account/types/AccountBriefRecord";
import {PortfolioType} from "../../portfolio/models/Portfolio";
import {Branding} from "../../types/Branding";
import {getCurrentEnvironment, getGTMContainerId, initGTM} from "../util/analyticUtil";
import {pushDataLayer} from "../util/pushDataLayer";
import {ClickEvent, PageView, VIRTUAL_PAGE_VIEWS, VirtualPageView} from "../models/VirtualPageViews";
import {GTMCustomEventParameters} from "../models/GTMCustomEventParameters";
import {marketTime} from "../../js/util/DateTimeUtil";
import {calculatePageTitle} from "../../dashboard/routing/OMDashboardRouterConfig";
import {PlaceOrderEvent} from "../models/CustomEvents";

export class GTMConversionEventService {

    public static id = "gtmConversionEventService";
    public isOpenMarkets: boolean;

    /** @ngInject */
    public constructor(private userConfig: UserConfig, private readonly accountStore: AccountStore) {
        this.isOpenMarkets = userConfig.branding === Branding.OPEN_MARKETS;
    }

    private hasNoBrokerAccount(accounts: AccountBriefRecords): boolean {
        return accounts.every((a) => a.portfolioType !== PortfolioType.BROKER);
    }

    public sendConversionEvent() {
        const containerId = getGTMContainerId(this.userConfig.branding);
        initGTM(containerId);
    }

    public sendVirtualPageView(virtualPageView: VirtualPageView) {
        const { path, hostUrl, title, timestamp } = virtualPageView;
        if (timestamp !== undefined) {
            this.sendCustomEvent({
                                     event: "VirtualPageView",
                                     pagePath: path,
                                     hostUrl,
                                     pageTitle: title,
                                     timestamp});
        } else {
            this.sendCustomEvent({
                                     event: "VirtualPageView",
                                     pagePath: path,
                                     hostUrl,
                                     pageTitle: title});
        }
    }

    public sendPageView(pageView: PageView) {
        const { path, hostUrl, title, timestamp } = pageView;
        if (timestamp !== undefined) {
            this.sendCustomEvent({
                                     event: "pageView",
                                     pagePath: path,
                                     hostUrl,
                                     pageTitle: title,
                                     timestamp});
        } else {
            this.sendCustomEvent({
                                     event: "pageView",
                                     pagePath: path,
                                     hostUrl,
                                     pageTitle: title});
        }
    }

    public sendClickEvent(clickEvent: ClickEvent) {
        const { path, hostUrl, title, timestamp, fromLocation } = clickEvent;
        if (timestamp !== undefined) {
            this.sendCustomEvent({
                                     event: "click",
                                     pagePath: path,
                                     hostUrl,
                                     pageTitle: title,
                                     fromLocation,
                                     timestamp});
        } else {
            this.sendCustomEvent({
                                     event: "click",
                                     pagePath: path,
                                     hostUrl,
                                     fromLocation,
                                     pageTitle: title});
        }
    }

    public sendPlaceOrderEvent(placeOrderEvent: PlaceOrderEvent) {
        const { path, hostUrl, title } = placeOrderEvent;
        this.sendCustomEvent({
                                 event: "placeOrder",
                                 pagePath: path,
                                 hostUrl,
                                 pageTitle: title});
    }

    public sendUserIDView() {
        const userId = this.userConfig.uid;
        if (userId !== undefined || userId !== "") {
            pushDataLayer("login", {userId});
        }
    }

    public sendCustomEvent(params: GTMCustomEventParameters) {
        const userId = this.userConfig.uid;
        const userSkin = this.userConfig.branding;
        const pageTitle = calculatePageTitle(this.userConfig.branding);
        const environment = getCurrentEnvironment();
        if (userId !== undefined || userId !== "") {
            pushDataLayer(params.event,
                          {
                              pageTitle: params.pageTitle ?? pageTitle,
                              pagePath: params.pagePath,
                              host: params.hostUrl,
                              fromLocation: params.fromLocation,
                              virtualPageTitle: params.pageTitle ?? pageTitle,
                              virtualPageHost: params.hostUrl,
                              virtualPagePath: params.pagePath,
                              userId,
                              userSkin,
                              environment,
                              timestamp: marketTime().format("DD/MM/YYYY, HH:mm")
                          });
        }
    }
}
